import { useCallback, useEffect, useState } from "react"
import { config } from "../auth/shared/config"

// Hands Ask Jay questions to the embedding portal, which owns Jay. Inert until an allowed parent answers.
// Mirror of platform-frontend `features/Safety/AskJay/useLegacyPortalAskJay.ts` — change both together.
export const ASK_JAY_PROTOCOL_VERSION = 1

export type AskJaySuggestion = Readonly<{ id: string; label: string }>

export type AskJayConfig = Readonly<{ suggestions: ReadonlyArray<AskJaySuggestion> }>

export type AskJayRequest =
  | Readonly<{ input: `typed`; prompt: string }>
  | Readonly<{ input: `suggestion`; suggestionId: string }>

type ConfigMessage = Readonly<{
  type: `ASK_JAY_CONFIG`
  v: number
  policyId: string
  enabled: boolean
  suggestions: ReadonlyArray<AskJaySuggestion>
}>

const isSuggestion = (value: unknown): value is AskJaySuggestion =>
  typeof value === `object` &&
  value !== null &&
  typeof (value as AskJaySuggestion).id === `string` &&
  typeof (value as AskJaySuggestion).label === `string`

const isConfigMessage = (value: unknown): value is ConfigMessage => {
  if (typeof value !== `object` || value === null) return false
  const message = value as ConfigMessage
  return (
    message.type === `ASK_JAY_CONFIG` &&
    message.v === ASK_JAY_PROTOCOL_VERSION &&
    typeof message.policyId === `string` &&
    typeof message.enabled === `boolean` &&
    Array.isArray(message.suggestions) &&
    message.suggestions.every(isSuggestion)
  )
}

// The parent may not be listening yet, so the request repeats until answered.
export const CONFIG_REQUEST_ATTEMPTS = 5
export const CONFIG_REQUEST_INTERVAL_MS = 1000

type Connection = Readonly<{ policyId: string; config: AskJayConfig; parentOrigin: string }>

export const useAskJayBridge = (policyId: string) => {
  const [stored, setConnection] = useState<Connection | null>(null)
  // A connection from a previous policy must not count until the reset below lands.
  const connection = stored !== null && stored.policyId === policyId ? stored : null

  useEffect(() => {
    setConnection(null)
    if (!config().embedded) return undefined

    let attempts = 0
    let timer: ReturnType<typeof setInterval> | undefined

    const listener = (event: MessageEvent): void => {
      if (event.source !== window.parent) return
      if (!isConfigMessage(event.data)) return
      if (!config().isAllowedOrigin(event.origin)) return
      if (event.data.policyId !== policyId) return

      clearInterval(timer)
      setConnection(
        event.data.enabled
          ? { policyId, config: { suggestions: event.data.suggestions }, parentOrigin: event.origin }
          : null
      )
    }

    const requestConfig = (): void => {
      attempts += 1
      if (attempts > CONFIG_REQUEST_ATTEMPTS) {
        clearInterval(timer)
        return
      }
      // `*` because the parent's origin isn't known until it replies; the request carries nothing.
      window.parent.postMessage({ type: `ASK_JAY_REQUEST_CONFIG`, v: ASK_JAY_PROTOCOL_VERSION }, `*`)
    }

    window.addEventListener(`message`, listener, false)
    requestConfig()
    timer = setInterval(requestConfig, CONFIG_REQUEST_INTERVAL_MS)

    return () => {
      window.removeEventListener(`message`, listener, false)
      clearInterval(timer)
    }
  }, [policyId])

  const ask = useCallback(
    (request: AskJayRequest): void => {
      if (connection === null) return
      window.parent.postMessage(
        { type: `ASK_JAY`, v: ASK_JAY_PROTOCOL_VERSION, policyId, ...request },
        connection.parentOrigin
      )
    },
    [connection, policyId]
  )

  const reportShown = useCallback((): void => {
    if (connection === null) return
    window.parent.postMessage(
      { type: `ASK_JAY_BAR_SHOWN`, v: ASK_JAY_PROTOCOL_VERSION, policyId },
      connection.parentOrigin
    )
  }, [connection, policyId])

  return { config: connection?.config ?? null, ask, reportShown }
}
